/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { PaginationPageLimit } from '@squiz/dxp-ai-types';
import {
  ALERT_TYPE_REPORT,
  API_TABLE_EVENT_PAGE_NEXT,
  Alert,
  ApiTable,
  ApiTableProps,
  EmptyState,
  ICON_MENU_BOOK,
  Link,
  SPINNER_TYPE_REGULAR,
  SdsIcon,
  Spinner,
} from '@squiz/sds';
import { getCapabilityPath } from 'apps/console-core/src/Utilities/GetCapabilityPath/GetCapabilityPath';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AI_STRINGS } from '../../Constants/Strings.constants';
import { useContexts } from '../../Services/ai';
import { getAiContextPath } from '../../Utilities/getAiContextPath';
import { AiContextTableProps } from './AiContextTable.props';

export const AiContextTable = (props: AiContextTableProps): JSX.Element => {
  const { ai } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const labels = {
    ...props.labels,
    descriptionColumn: t(AI_STRINGS.description),
    idColumn: t(AI_STRINGS.id),
    nameColumn: t(AI_STRINGS.name),
  };
  const [pageSize, setPageSize] = useState(PaginationPageLimit.small);
  const [cursor, setCursor] = useState<string | undefined>();
  const [contexts, error, state] = useContexts(ai.id, pageSize, cursor);
  const nextPage = contexts?.links?.nextPage;
  const prevPage = contexts?.links?.prevPage;
  const rows =
    contexts?.items.map((row) => {
      const url = getCapabilityPath(getAiContextPath(ai.type, ai.id, row.id));

      return {
        description: row.description,
        id: row.id,
        name: (
          <Link
            classes="view-context-link"
            hideIcon
            href={url}
            onClick={(e) => {
              // TODO: this is so the URL pushes to the browser state instead of reloading the page.
              // Ideally this should behave similar to the react-router-dom Link component, ie:
              //  * External links redirect.
              //  * Internal links push to browser state.
              //  * Command+Click opens in new window.
              e.preventDefault();
              navigate(url);
            }}
            text={row.name}
          />
        ),
        rowId: row.id,
      };
    }) || [];
  const tableProps: ApiTableProps = {
    fetchData: (options) => {
      setPageSize(options.pageSize);
      setCursor(options.eventType === API_TABLE_EVENT_PAGE_NEXT ? nextPage : prevPage);
    },
    isSlim: true,
    loading: state === 'pending',
    pageTokens: {
      pageAfter: nextPage,
      pageBefore: prevPage,
    },
    perPageDefault: pageSize,
    perPageOptions: [PaginationPageLimit.small, PaginationPageLimit.medium, PaginationPageLimit.large],
    tableContent: {
      columns: [
        { content: labels.nameColumn, key: 'name' },
        { content: labels.idColumn, key: 'id' },
        { content: labels.descriptionColumn, key: 'description' },
      ],
      rows: rows,
    },
  };

  return (
    <div className="content-tool-table-container ai-context-table" data-id="AI Context Table">
      <div className="content-tool-table-container__header">
        <div className="header-text">
          <SdsIcon svg={ICON_MENU_BOOK}></SdsIcon>
          <span>{labels.heading}</span>
        </div>
        <p className="content-tool-table-container__table-description">{labels.description}</p>
      </div>
      <div className="content-tool-table-container__table ai_thread_table">
        {state === 'pending' && !rows.length && (
          <div className="loading">
            <Spinner type={SPINNER_TYPE_REGULAR} />
          </div>
        )}
        {state === 'error' && <Alert message={String(error)} type={ALERT_TYPE_REPORT} />}
        {state !== 'pending' && rows.length === 0 && <EmptyState caption={labels.emptyText} isLite />}
        {state !== 'error' && rows.length > 0 && <ApiTable {...tableProps} />}
      </div>
    </div>
  );
};
